import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import OfferCard from "@/components/OfferCard";
import FavoriteButton from "@/components/FavoriteButton";
import { Store, MapPin, Tag, Heart } from "lucide-react";

export default function Favorites() {
  const [data, setData] = useState({ offers: [], businesses: [], categories: [], cities: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/favorites/all");
      setData(data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const empty = !loading && data.offers.length === 0 && data.businesses.length === 0
                && data.categories.length === 0 && data.cities.length === 0;

  return (
    <div data-testid="favorites-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold mb-4 flex items-center gap-2">
        <Heart className="text-rose-500" size={24}/> Mes favoris
      </h1>

      {loading && <p className="text-slate-500 text-sm">Chargement...</p>}

      {empty && (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center" data-testid="favorites-empty">
          <Heart className="mx-auto text-slate-300" size={40}/>
          <div className="mt-2 text-slate-500 text-sm">Aucun favori pour l'instant.</div>
          <Link to="/bons-plans" className="mt-3 inline-block text-[#E05638] font-semibold hover:underline">Découvrir les bons plans</Link>
        </div>
      )}

      {/* Offres */}
      {data.offers.length > 0 && (
        <section className="mb-8" data-testid="favorites-offers">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2 mb-3"><Tag size={18} className="text-[#E05638]"/> Offres ({data.offers.length})</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.offers.map((o) => (<OfferCard key={o.id} offer={o} onUnfav={load}/>))}
          </div>
        </section>
      )}

      {/* Commerces */}
      {data.businesses.length > 0 && (
        <section className="mb-8" data-testid="favorites-businesses">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2 mb-3"><Store size={18} className="text-emerald-700"/> Commerces ({data.businesses.length})</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.businesses.map((b) => (
              <div key={b.id} className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`favorites-biz-${b.id}`}>
                <Link to={`/commerce/${b.id}`} className="block">
                  <div className="aspect-[4/3] bg-slate-100">
                    {b.cover_image ? <img src={b.cover_image} alt={b.name} className="w-full h-full object-cover" loading="lazy"/> : <div className="w-full h-full grid place-items-center text-slate-300"><Store size={36}/></div>}
                  </div>
                  <div className="p-4">
                    <div className="font-heading font-semibold text-slate-900 line-clamp-1">{b.name}</div>
                    <div className="mt-1 text-xs text-slate-500 flex items-center gap-1"><MapPin size={11}/> {b.city}</div>
                  </div>
                </Link>
                <div className="absolute top-3 right-3">
                  <FavoriteButton type="business" id={b.id} onToggle={load}/>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Catégories */}
      {data.categories.length > 0 && (
        <section className="mb-8" data-testid="favorites-categories">
          <h2 className="font-heading text-xl font-bold mb-3">Catégories ({data.categories.length})</h2>
          <div className="flex flex-wrap gap-2">
            {data.categories.map((c) => (
              <div key={c.slug} className="relative inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-4 pr-2 py-1.5" data-testid={`favorites-cat-${c.slug}`}>
                <Link to={`/bons-plans?category=${c.slug}`} className="text-sm font-semibold text-slate-800 hover:text-[#E05638]">{c.name}</Link>
                <FavoriteButton type="category" id={c.slug} size="sm" onToggle={load}/>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Villes */}
      {data.cities.length > 0 && (
        <section className="mb-8" data-testid="favorites-cities">
          <h2 className="font-heading text-xl font-bold mb-3">Villes ({data.cities.length})</h2>
          <div className="flex flex-wrap gap-2">
            {data.cities.map((c) => (
              <div key={c.name} className="relative inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-4 pr-2 py-1.5" data-testid={`favorites-city-${c.name}`}>
                <Link to={`/bons-plans?city=${encodeURIComponent(c.name)}`} className="text-sm font-semibold text-slate-800 hover:text-[#E05638] inline-flex items-center gap-1"><MapPin size={12}/>{c.name}</Link>
                <FavoriteButton type="city" id={c.name} size="sm" onToggle={load}/>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
